import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import type {
  SocialTrackerDialogProps,
  TrackerDrafts,
  TrackerFormState,
  TrackerStep,
} from '@/types/components/social-tracker-dialog'
import { SOCIAL_TRACKER_LABEL_KINDS } from '@digital-twins/shared-types'
import { useSocialListeningApi } from './SocialListeningApiContext'
import {
  addSocialTerms,
  initialFormState,
  labelValues,
  missingRequiredFields,
  toTrackerInput,
  trackerLabels,
  withLabelValues,
  MAX_TRACKER_TERMS,
  MAX_TRACKER_TERM_LENGTH,
  MAX_TRACKER_LABELS,
} from './socialTrackerForm'

const emptyDrafts: TrackerDrafts = {
  keywords: '',
  handles: '',
  brand: '',
  market: '',
  category: '',
}

export function useSocialTrackerWizard({
  tracker,
  initialProposal,
  onOpenChange,
  onSaved,
}: SocialTrackerDialogProps) {
  const { t } = useTranslation('app')
  const api = useSocialListeningApi()
  const [form, setForm] = useState(() =>
    initialFormState(tracker, initialProposal ?? null),
  )
  const [drafts, setDrafts] = useState<TrackerDrafts>(emptyDrafts)
  const [step, setStep] = useState<TrackerStep>(
    tracker || initialProposal ? 'settings' : 'topics',
  )
  const [activeKeyword, setActiveKeyword] = useState<string | null>(
    form.keywords[0] ?? null,
  )
  const [hasTriedSubmit, setHasTriedSubmit] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isPreparing, setIsPreparing] = useState(false)
  const [preparationFailed, setPreparationFailed] = useState(false)
  const preparing = useRef(false)
  const requestId = useRef(0)
  const preparedRequest = useRef<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const alive = useRef(true)
  const saving = useRef(false)
  const dirty = useRef(new Set<keyof TrackerFormState>())
  const dirtyBrief = useRef(new Set<keyof TrackerFormState['brief']>())
  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
      requestId.current += 1
    }
  }, [])

  function updateField<K extends keyof TrackerFormState>(
    key: K,
    value: TrackerFormState[K],
  ) {
    dirty.current.add(key)
    if (key === 'brief') {
      const brief = value as TrackerFormState['brief']
      for (const field of ['include', 'exclude'] as const) {
        if (brief[field] !== form.brief[field]) dirtyBrief.current.add(field)
      }
    }
    setForm((previous) => ({ ...previous, [key]: value }))
    setError(null)
    setPreparationFailed(false)
  }
  function updateDraft(key: keyof TrackerDrafts, value: string) {
    setDrafts((previous) => ({ ...previous, [key]: value }))
    setError(null)
    setPreparationFailed(false)
  }
  // Resolve every pending literal term into one snapshot before navigation or save.
  function flushDrafts() {
    let next = {
      ...form,
      keywords: addSocialTerms(form.keywords, drafts.keywords),
      handles: addSocialTerms(form.handles, drafts.handles),
    }
    for (const kind of ['brand', 'category'] as const) {
      next = {
        ...next,
        labels: withLabelValues(
          next.labels,
          kind,
          addSocialTerms(labelValues(next.labels, kind), drafts[kind]),
        ),
      }
    }
    if (drafts.handles.trim()) dirty.current.add('handles')
    if (SOCIAL_TRACKER_LABEL_KINDS.some((kind) => drafts[kind].trim()))
      dirty.current.add('labels')
    next = { ...next, labels: trackerLabels(next) }
    setForm(next)
    setDrafts(emptyDrafts)
    return next
  }
  function validateTerms(next: TrackerFormState) {
    if (
      next.keywords.length > MAX_TRACKER_TERMS ||
      next.handles.length > MAX_TRACKER_TERMS ||
      next.labels.length > MAX_TRACKER_LABELS ||
      [
        ...next.keywords,
        ...next.handles,
        ...next.labels.map((label) => label.value),
      ].some((term) => term.length > MAX_TRACKER_TERM_LENGTH)
    ) {
      setError(t('socialListening.wizard.termLimits'))
      return false
    }
    return true
  }
  function goToTopics() {
    flushDrafts()
    setStep('topics')
    setError(null)
  }
  async function goToSettings() {
    if (preparing.current) return
    const next = flushDrafts()
    setHasTriedSubmit(true)
    if (!next.keywords.length || !validateTerms(next)) return
    const seed = {
      keywords: next.keywords,
      keywordNotes: next.keywords.flatMap((keyword) => {
        const note = next.keywordNotes.find(
          (entry) => entry.keyword === keyword,
        )
        return note?.description.trim() ? [note] : []
      }),
    }
    const request = {
      seed,
      ...(dirty.current.has('platform') ? { platform: next.platform } : {}),
    }
    const fingerprint = JSON.stringify(request)
    // Existing trackers and Ask proposals already own their complete configuration.
    if (tracker || initialProposal || preparedRequest.current === fingerprint) {
      setHasTriedSubmit(false)
      setStep('settings')
      setError(null)
      return
    }
    preparing.current = true
    setIsPreparing(true)
    setPreparationFailed(false)
    setError(null)
    const id = ++requestId.current
    try {
      const proposal = await api.suggestTracker(request)
      if (!alive.current || id !== requestId.current) return
      const suggestedBrief = proposal.brief
      if (
        !suggestedBrief ||
        typeof suggestedBrief.include !== 'string' ||
        typeof suggestedBrief.exclude !== 'string'
      ) {
        throw new Error('Suggestion is missing relevance rules')
      }
      // Apply suggested settings and rules only to fields the user has not edited.
      setForm((current) => ({
        ...current,
        ...(!dirty.current.has('name') ? { name: proposal.name } : {}),
        ...(!dirty.current.has('platform')
          ? { platform: proposal.platform }
          : {}),
        ...(!dirty.current.has('market')
          ? { market: proposal.market, marketCode: null }
          : {}),
        ...(!dirty.current.has('language')
          ? { language: proposal.language }
          : {}),
        ...(!dirty.current.has('labels') ? { labels: proposal.labels } : {}),
        brief: {
          include: dirtyBrief.current.has('include')
            ? current.brief.include
            : suggestedBrief.include,
          exclude: dirtyBrief.current.has('exclude')
            ? current.brief.exclude
            : suggestedBrief.exclude,
        },
      }))
      preparedRequest.current = fingerprint
      setHasTriedSubmit(false)
      setStep('settings')
    } catch {
      if (!alive.current || id !== requestId.current) return
      setPreparationFailed(true)
      setError(t('socialListening.wizard.prepareFailed'))
    } finally {
      if (alive.current && id === requestId.current) {
        preparing.current = false
        setIsPreparing(false)
      }
    }
  }
  async function save() {
    if (saving.current || preparing.current) return
    // Every tracker is a channel of a Topic now: its config is edited through
    // the Topic, and the server refuses a channel PATCH (409).
    if (tracker) {
      setError(t('socialListening.topicToast.editInTopic'))
      return
    }
    const next = flushDrafts()
    setHasTriedSubmit(true)
    if (!validateTerms(next)) return
    if (missingRequiredFields(next).length) {
      if (!next.keywords.length) setStep('topics')
      return
    }
    saving.current = true
    setIsSaving(true)
    setError(null)
    try {
      const saved = await api.createTracker(toTrackerInput(next))
      if (!alive.current) return
      onSaved(saved)
      toast.success(t('socialListening.toast.created'))
      onOpenChange(false)
    } catch (cause: unknown) {
      if (!alive.current) return
      const message = (cause as { response?: { data?: { message?: unknown } } })
        ?.response?.data?.message
      setError(
        typeof message === 'string'
          ? message
          : t('socialListening.form.saveFailed'),
      )
    } finally {
      if (alive.current) {
        saving.current = false
        setIsSaving(false)
      }
    }
  }
  return {
    form,
    drafts,
    step,
    activeKeyword,
    setActiveKeyword,
    updateField,
    updateDraft,
    goToTopics,
    goToSettings,
    save,
    isSaving,
    isPreparing,
    preparationFailed,
    error,
    missing: hasTriedSubmit ? missingRequiredFields(form) : [],
  }
}
